import { toast } from 'sonner';
import { createComponentLogger } from './logger';
import { windowMessenger } from '@/shared/services/windowMessenger';
import { supabase } from './supabase';

const log = createComponentLogger('WindowOpenHelper', 'ui');
const ARTEMIS_PATH = '/internal/artemis';
let artemisWindowRef: Window | null = null;

/**
 * Opens a window with popup blocker detection and fallback
 */
export function openWindow(url: string, target = '_blank'): Window | null {
  try {
    log.debug('Attempting to open window', { url });
    
    // Focus the current window to ensure we're in user interaction context
    window.focus();
    
    // Try to open the window
    const newWindow = window.open(url, target, 'noopener,noreferrer');
    
    // Check if popup was blocked
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Popup was blocked
      log.warn('Popup was blocked', { url });
      
      // Show toast with action button
      toast.error('Popup blocked. Click here to open the preview.', {
        duration: 10000,
        action: {
          label: 'Open Preview',
          onClick: () => {
            window.open(url, target);
          }
        }
      });
      
      // Also copy link to clipboard as backup
      navigator.clipboard.writeText(url).then(() => {
        toast.info('Preview link copied to clipboard', { duration: 3000 });
      });
      
      return null;
    }
    
    // Success
    log.debug('Successfully opened window', { url });
    return newWindow;
  } catch (error) {
    log.error('Error opening window', { url, error: error instanceof Error ? error.message : String(error) });
    
    // Show error toast with action
    toast.error('Could not open preview window.', {
      duration: 10000,
      action: {
        label: 'Try Again',
        onClick: () => {
          window.open(url, target);
        }
      }
    });
    
    return null;
  }
}

/**
 * Opens Artemis as a same-origin standalone internal tool in a new tab.
 *
 * Implementation note: passing only (url, target) — no third `windowFeatures`
 * arg — tells the browser to open in a regular tab rather than a detached
 * popup, by default in the same browser window. (Browser/user settings can
 * still steer placement; this is the default we target.) The named target
 * `'lendwise-artemis'` makes subsequent calls focus the existing tab opened
 * by this helper instead of spawning a duplicate.
 *
 * This intentionally does not generate, append, or postMessage auth tokens.
 * Artemis relies on the existing protected /internal/artemis route and the
 * browser's same-origin session storage.
 *
 * `path` opens a specific Artemis view (for example Online Leads). It loads
 * that view in the same named tab, even when Artemis is already open there;
 * the plain call keeps focusing the existing tab as it is.
 */
export function openArtemisWindow(path = ARTEMIS_PATH): void {
  const url = `${window.location.origin}${path}`;
  const target = 'lendwise-artemis';

  try {
    log.debug('Opening Artemis tab', { url });
    window.focus();

    if (artemisWindowRef && !artemisWindowRef.closed && path === ARTEMIS_PATH) {
      artemisWindowRef.focus();
      log.info('Focused existing Artemis tab');
      return;
    }

    const artemisWindow = window.open(url, target);

    if (!artemisWindow || artemisWindow.closed || typeof artemisWindow.closed === 'undefined') {
      log.warn('Artemis tab open was blocked', { url });

      toast.error('Could not open Artemis. Please allow popups for this site.', {
        duration: 15000,
        action: {
          label: 'Open Artemis',
          onClick: () => {
            window.open(url, target);
          }
        },
        description: 'Your browser blocked opening Artemis. Click the button above or allow popups for this site.'
      });

      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => {
          toast.info('Artemis URL copied to clipboard', {
            duration: 3000,
            description: 'You can paste this URL into a new tab to open Artemis.'
          });
        }).catch(() => {
          log.warn('Could not copy Artemis URL to clipboard');
        });
      }

      return;
    }

    artemisWindowRef = artemisWindow;
    artemisWindow.focus();
    log.info('Artemis tab opened successfully');
  } catch (error) {
    log.error('Error opening Artemis tab', {
      error: error instanceof Error ? error.message : String(error)
    });

    toast.error('Failed to open Artemis', {
      duration: 10000,
      action: {
        label: 'Try Again',
        onClick: () => {
          window.open(url, target);
        }
      },
      description: 'There was an error opening Artemis. Please try again or contact support.'
    });
  }
}

/** Open the permission-protected Marketing Studio with an isolated opener. */
export function openMarketingStudioWindow(): void {
  const path = '/marketing';
  const url = `${window.location.origin}${path}`;

  try {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.hidden = true;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    log.error(
      'Error opening Marketing Studio tab',
      { operation: 'openMarketingStudioWindow' },
      error instanceof Error ? error : new Error(String(error))
    );
    toast.error('Failed to open Marketing Studio.');
  }
}

/**
 * Generates a temporary authentication token for cross-window auth
 */
async function generateTempAuthToken(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      log.warn('No active session for auth token generation');
      return null;
    }
    
    // For now, we'll use the access token directly
    // In production, you'd want to generate a short-lived token
    return session.access_token;
  } catch (error) {
    log.error('Failed to generate auth token', { error });
    return null;
  }
}

/**
 * Opens the Market Dashboard in a new window with optimized settings
 */
export async function openMarketDashboard(): Promise<void> {
  // Use environment variable if set, otherwise use relative path
  const configuredUrl = import.meta.env.VITE_MARKET_DASHBOARD_URL;
  const baseUrl = configuredUrl || '/market-dashboard.html';
  
  // Construct full URL from relative path if needed
  const fullUrl = baseUrl.startsWith('http') 
    ? baseUrl 
    : `${window.location.origin}${baseUrl}`;
  
  log.debug('Opening Market Dashboard', { baseUrl, fullUrl });
  
  // Optimal window dimensions for Market Dashboard
  const windowFeatures = [
    'width=1400',
    'height=900',
    'left=100',
    'top=100',
    'scrollbars=yes',
    'resizable=yes',
    'status=no',
    'toolbar=no',
    'menubar=no',
    'location=no'
  ].join(',');
  
  try {
    // Generate auth token for the new window
    const authToken = await generateTempAuthToken();
    const url = authToken 
      ? `${fullUrl}?token=${encodeURIComponent(authToken)}`
      : fullUrl;
    
    log.debug('Attempting to open Market Dashboard window', { url: fullUrl, hasAuth: !!authToken });
    
    // Focus the current window to ensure we're in user interaction context
    window.focus();
    
    // Try to open the Market Dashboard window
    const newWindow = window.open(url, 'marketDashboard', windowFeatures);
    
    // Check if popup was blocked
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Popup was blocked
      log.warn('Market Dashboard popup was blocked');
      
      // Show toast with action button
      toast.error('Popup blocked. Please allow popups to open Market Dashboard.', {
        duration: 15000,
        action: {
          label: 'Open Market Dashboard',
          onClick: () => {
            window.open(url, '_blank');
          }
        },
        description: 'Your browser blocked the Market Dashboard popup. Click the button above or allow popups for this site.'
      });
      
      // Also copy link to clipboard as backup (without auth token)
      if (navigator.clipboard) {
        navigator.clipboard.writeText(baseUrl).then(() => {
          toast.info('Market Dashboard URL copied to clipboard', { 
            duration: 3000,
            description: 'You can paste this URL into a new tab to access the Market Dashboard.'
          });
        }).catch(() => {
          // Clipboard access failed, but that's okay
          log.warn('Could not copy to clipboard');
        });
      }
      
      return;
    }
    
    // Success - window opened
    log.info('Market Dashboard opened successfully');
    
    // Focus the new window
    newWindow.focus();
    
    // Register the window with the messenger service
    windowMessenger.registerWindow('market-dashboard', newWindow);
    
    // Send initial auth state
    if (authToken) {
      setTimeout(() => {
        windowMessenger.sendMessage('market-dashboard', {
          type: 'AUTH_UPDATE',
          payload: { token: authToken }
        });
      }, 1000); // Wait for window to initialize
    }
    
    // Show success message
    toast.success('Market Dashboard opened successfully', {
      duration: 3000,
      description: 'The Market Dashboard has been opened in a new window.'
    });
    
  } catch (error) {
    log.error('Error opening Market Dashboard', { error: error instanceof Error ? error.message : String(error) });
    
    // Show error toast with action
    toast.error('Failed to open Market Dashboard', {
      duration: 10000,
      action: {
        label: 'Try Again',
        onClick: () => {
          window.open(baseUrl, '_blank');
        }
      },
      description: 'There was an error opening the Market Dashboard. Please try again or contact support.'
    });
  }
}
